import { Banner, TextLink } from '@neo4j-ndl/react';
import { APP_SCOPE, QUERY_TYPE } from '@nx/constants';
import type { Instance } from '@nx/state';
import { useActiveDatabase, useConnection, useConnectionSelectorModal, useCypherQuery } from '@nx/state';
import { useMemo, useState } from 'react';

import { BtreeIndexQueryModal } from './btree-index-query-modal';
import type { IndexToFix } from './fix-index';
import { IndexesTable } from './indexes-table';

const ALL_INDEXES = `CALL {
  CALL db.indexes()
  YIELD name, type, entityType, labelsOrTypes, properties
  WHERE type = "RANGE" OR type = "TEXT" OR type = "POINT"
  RETURN entityType, labelsOrTypes, properties, name
  UNION
  RETURN "" AS entityType, [] AS labelsOrTypes, [] AS properties, "" AS name
}
WITH collect({entityType: entityType, labelsOrTypes: labelsOrTypes, properties: properties, name: name}) as collectionRTPIndexes
CALL {
  CALL db.constraints()
  YIELD name
  RETURN collect(name) AS collectionConstraintNames
}
CALL db.indexes()
YIELD name, state, type, entityType, labelsOrTypes, properties
WHERE type = "BTREE"
RETURN name, state, name IN collectionConstraintNames as constraintBacking, type, entityType, labelsOrTypes, properties, 
[ replacement in collectionRTPIndexes where 
   replacement.entityType = entityType 
   and replacement.labelsOrTypes = labelsOrTypes 
   and replacement.properties = properties][0].name as replacementName`;

export const DeprecatedIndexTypes = ({ instance }: { instance?: Instance }) => {
  const [isBtreeIndexQueryModalOpen, setIsBtreeIndexQueryModalOpen] = useState(false);

  const { status: activeConnectionStatus, metadata: activeConnectionMetadata } = useConnection();
  const activeDatabase = useActiveDatabase();

  const alreadyConnectedToThisInstance = useMemo(() => {
    return (
      activeConnectionStatus.isConnected &&
      instance?.id === activeConnectionMetadata?.auraId &&
      activeDatabase?.name === 'neo4j'
    );
  }, [activeConnectionMetadata?.auraId, activeConnectionStatus.isConnected, activeDatabase?.name, instance?.id]);

  const connectionSelectorModal = useConnectionSelectorModal();

  const [indexesToFixCypherQuery, invalidateIndexesToFixCypherQuery] = useCypherQuery<IndexToFix>(
    {
      query: ALL_INDEXES,
    },
    {
      // TODO is UserAction correct?
      metadata: { appScope: APP_SCOPE.ops, queryType: QUERY_TYPE.UserAction },
      paused: !alreadyConnectedToThisInstance,
      transactionMode: 'read',
      sessionConfig: { database: 'neo4j' },
    },
  );

  const oldIndexes = useMemo(() => {
    if (!indexesToFixCypherQuery.records) {
      return [];
    }

    return indexesToFixCypherQuery.records.map((record) => ({
      name: record.get('name'),
      state: record.get('state'),
      type: record.get('type'),
      entityType: record.get('entityType'),
      labelsOrTypes: record.get('labelsOrTypes'),
      properties: record.get('properties'),
      constraintBacking: record.get('constraintBacking'),
      replacementName: record.get('replacementName'),
      replacementQueryFinished: false,
      replacementQueryError: false,
    }));
  }, [indexesToFixCypherQuery.records]);

  return (
    <div className="flex flex-col gap-2">
      <p>
        Before you can successfully migrate to the latest Aura version, you have to decide what happens to your existing
        BTREE indexes. BTREE indexes do not exist in the latest Aura version anymore and therefore you can decide to
        create replacement indexes before the migration (pre-create) or have the migration process create them for you.
        These replacement indexes can co-exist alongside the old BTREE indexes until you finally migrate. The migration
        process will automatically remove the old ones. This also applies to constraints that are backed by BTREE
        indexes.
      </p>
      <p>
        Please be aware that pre-creating indexes needs additional disk space to be available. Make sure your instance
        has enough space left before creating the replacement indexes and resize your instance if required. The space
        used by the old indexes will be freed up after the migration process.
      </p>
      <p>
        You can also choose to not pre-create indexes. Please be aware that this will lead to a longer migration process
        as well as migrate all currently existing BTREE Indexes to RANGE indexes. Depending on your application and data
        model this might not be optimal for you.
      </p>
      <p>
        {!alreadyConnectedToThisInstance && (
          <>
            To create replacement indexes using the migration assistant, you need to{' '}
            <TextLink
              className="cursor-pointer"
              htmlAttributes={{
                onClick: () => {
                  connectionSelectorModal.open({
                    instanceNameFilter: instance?.name,
                  });
                },
              }}
            >
              connect to the instance "{instance?.name}"
            </TextLink>{' '}
            first.
          </>
        )}
      </p>

      {indexesToFixCypherQuery.error && (
        <Banner
          description={`Error loading indexes: ${indexesToFixCypherQuery.error.message}.`}
          type="danger"
          hasIcon
          usage="inline"
        />
      )}
      {alreadyConnectedToThisInstance && indexesToFixCypherQuery.records && (
        <>
          {indexesToFixCypherQuery.records.length > 0 && (
            <>
              <p>
                The following table shows the replacement status of all relevant indexes of instance "{instance?.name}"{' '}
                and allows creating missing replacements.
              </p>
              <IndexesTable
                oldIndexes={oldIndexes}
                invalidateIndexesToFixCypherQuery={invalidateIndexesToFixCypherQuery}
                isLoading={indexesToFixCypherQuery.fetching}
              />
            </>
          )}
          {indexesToFixCypherQuery.records.length === 0 && (
            <p>
              According to the results of{' '}
              <TextLink
                className="cursor-pointer"
                htmlAttributes={{ onClick: () => setIsBtreeIndexQueryModalOpen(true) }}
              >
                this Cypher query
              </TextLink>{' '}
              executed on instance "{instance?.name}", there are no indexes that require replacement.
            </p>
          )}
        </>
      )}

      {!alreadyConnectedToThisInstance && (
        <p>
          Alternatively, you can use{' '}
          <TextLink className="cursor-pointer" htmlAttributes={{ onClick: () => setIsBtreeIndexQueryModalOpen(true) }}>
            this Cypher query
          </TextLink>{' '}
          to find the names of indexes without a replacement.
        </p>
      )}

      <p>
        For more details, see{' '}
        <TextLink
          type="external"
          href="https://neo4j.com/docs/upgrade-migration-guide/current/version-5/migration/planning/#_prepare_indexes"
        >
          Prepare indexes
        </TextLink>{' '}
        on how to choose replacement indexes and what Cypher queries to run to create those.
      </p>

      <BtreeIndexQueryModal isOpen={isBtreeIndexQueryModalOpen} onClose={() => setIsBtreeIndexQueryModalOpen(false)} />
    </div>
  );
};
